import React from "react";
import { useCreateSubscriptionMutation, useUpdateSubscriptionMutation } from "../../../../../redux/apiSlice";
import cogoToast from "cogo-toast";
import { Form, Formik } from "formik";
import { Modal, Form as FormLayout } from "react-bootstrap"
import * as Yup from "yup"
import TextInput from "../../../Common/components/text-input";
import SelectInput from "../../../Common/components/select-input";
import { SubscriptionIcon } from "../../../../util/Icons";
import { SubscriptionModel } from "./components/SubscriptionModel";
import { SubscriptionSubKey } from "./components/SubscriptionSubKey";

const EditSubscriptionModal = ({ showEditSubscriptionModal, onHide, details, tableRef, isNew, accountOptions = [] }) => {
    const [
        updateSubscription, // This is the mutation trigger
        {
            isLoading: isUpdating,
        }, // This is the destructured mutation result
    ] = useUpdateSubscriptionMutation()

    const [
        createSubscription, // This is the mutation trigger
        {
            isLoading: isCreating,
        }, // This is the destructured mutation result
    ] = useCreateSubscriptionMutation()

    const validateFlatFeeType = (feeType) => {
        return feeType === "Flat";
    }

    const validatePercentFeeType = (feeType) => {
        return feeType === "Percentage";
    }

    const validationSchema = Yup.object({
        accountId: Yup.string().required('Required'),
        product: Yup.string().required('Required'),
        frequency: Yup.string().required('Required'),
        useDefaultSubKey: Yup.bool().required('Required'),
        subKey: Yup.string().when('useDefaultSubKey', {
            is: (value) => !value,
            then: Yup.string().required('Required')
        }),
        useDefaultModel: Yup.bool().required('Required'),
        model: Yup.object().when('useDefaultModel', {
            is: (value) => !value,
            then: Yup.object().shape({
                "autoRenew": Yup.string().required('Required'),
                "currency": Yup.string().required('Required'),
                "tradeAbove": Yup.number().required('Required'),
                "trial": Yup.bool().required('Required'),
                "type": Yup.string().oneOf(["PREPAID", "POSTPAID"]).required('Required'),
                "maxBacklog": Yup.number().required('Required'),
                "flat": Yup.number().when('feeType', {
                    is: validateFlatFeeType,
                    then: Yup.number().min(0.1, 'Must be greater than or equal to 0.1').required('Required'),
                }),
                "max": Yup.number().when('feeType', {
                    is: validatePercentFeeType,
                    then: Yup.number().min(0.1, 'Must be greater than or equal to 0.1').required('Required'),
                }),
                "min": Yup.number().when('feeType', {
                    is: validatePercentFeeType,
                    then: Yup.number().min(0.1, 'Must be greater than or equal to 0.1').required('Required'),
                }),
                "percent": Yup.number().when('feeType', {
                    is: validatePercentFeeType,
                    then: Yup.number().min(0.1, 'Must be greater than or equal to 0.1').required('Required'),
                }),
                "feeType": Yup.string().oneOf(["Flat", "Percentage"]).required('Required'),
            }).required('Required')
        })
    })

    const compiledInitialValues = {
        accountId: details?.accountId || "",
        product: details?.product || "",
        frequency: details?.frequency || "",
        useDefaultSubKey: true,
        subKey: details?.subKey || "",
        useDefaultModel: true,
        model: {
            autoRenew: "",
            currency: "",
            tradeAbove: 0.0,
            trial: false,
            type: "",
            feeType: details?.model?.percent ? "Percentage" : "Flat",
            maxBacklog: 0,
            flat: 0.0,
            percent: 0.0,
            max: 0.0,
            min: 0.0,
            ...details?.model
        },
    }

    return (
        <Modal
            show={showEditSubscriptionModal}
            onHide={onHide}
            size="lg"
            aria-labelledby="contained-modal-title-vcenter"
            backdrop={(isUpdating || isCreating) ? "static" : true}
            keyboard={!isUpdating || !isCreating}
        >
            <Formik
                validationSchema={validationSchema}
                initialValues={compiledInitialValues}
                onSubmit={({
                    model: {
                        feeType,
                        ...model
                    },
                    ...values
                }) => {
                    if (feeType === "Flat") {
                        model["percent"] = 0
                        model["min"] = 0
                        model["max"] = 0
                    } else {
                        model["flat"] = 0
                    }

                    if (isNew) {
                        createSubscription({
                            userId: details?.userId,
                            accountId: values?.accountId,
                            useDefaultSubKey: values?.useDefaultSubKey,
                            subKey: values?.subKey,
                            product: values?.product,
                            frequency: values?.frequency,
                            useDefaultModel: values?.useDefaultModel,
                            model: model,
                        })
                            .unwrap()
                            .then(() => {
                                cogoToast.success("Created 'Create Subscription' action successfully")
                                tableRef.current.refresh()
                                onHide()
                            })
                    } else {
                        updateSubscription({
                            userId: details?.userId,
                            subId: details?.id,
                            subKey: values?.subKey,
                            product: values?.product,
                            frequency: values?.frequency,
                            model: model,
                            meta: details,
                        })
                            .unwrap()
                            .then(() => {
                                cogoToast.success("Created 'Update Subscription' action successfully")
                                tableRef.current.refresh()
                                onHide()
                            })
                    }
                }}
            >
                {({ isValid, values: { useDefaultModel, useDefaultSubKey, model, subKey }, setFieldValue }) =>
                    <Form>
                        <Modal.Header>
                            <Modal.Title id="contained-modal-title-vcenter">
                                <span className={`svg-icon svg-icon-lg svg-icon-primary mr-3`}>
                                    <SubscriptionIcon />
                                </span>
                                {isNew ? "Create" : "Edit"} Subscription
                            </Modal.Title>
                            <button type="button" className="close" onClick={onHide}>
                                <span aria-hidden="true" className="d-block">×</span>
                                <span className="sr-only">Close</span>
                            </button>
                        </Modal.Header>

                        <Modal.Body>
                            <FormLayout.Row>
                                {
                                    isNew &&
                                    <SelectInput
                                        label="Account*"
                                        name="accountId"
                                        options={accountOptions.map(({ accountId, partner, accountNumber }) => {
                                            return { name: `${partner}: ${accountNumber}`, value: accountId }
                                        })}
                                    />
                                }
                                <TextInput
                                    label="Product*"
                                    name="product"
                                    type="text"
                                />
                                <SelectInput
                                    label="Frequency*"
                                    name="frequency"
                                    options={[{ name: "Monthly", value: "MONTHLY" }, { name: "Annually", value: "ANNUALLY" }]}
                                />
                            </FormLayout.Row>
                            <div className="separator separator-dashed my-2" />
                            <SubscriptionSubKey setFieldValue={setFieldValue} subKey={subKey} useDefaultSubKey={useDefaultSubKey} />
                            <div className="separator separator-dashed my-2" />
                            <SubscriptionModel setFieldValue={setFieldValue} model={model} useDefaultModel={useDefaultModel} />
                        </Modal.Body>

                        <Modal.Footer>
                            <button
                                type="button"
                                onClick={onHide}
                                disabled={isUpdating}
                                className="btn btn-light btn-elevate px-4"
                            >
                                Cancel
                            </button>
                            <button
                                type="submit"
                                disabled={isUpdating || isCreating || !isValid}
                                className="btn btn-primary btn-elevate px-4 ml-4"
                            >
                                {isNew ? "Create" : "Update"} Subscription
                            </button>
                        </Modal.Footer>
                    </Form>
                }
            </Formik>
        </Modal>
    );
}

export default EditSubscriptionModal;
